import { ChangeEvent, useEffect, useState } from 'react'

import { Card } from '@/components/Card'
import { IconContainer } from '@/components/IconContainer'
import { Input } from '@/components/Input'
import { SlugInput } from '@/components/SlugInput'
import { FaviconFetcher } from '@/components/FaviconFetcher'
import { ErrorMsg } from '@/components/ErrorMsg'
import { useDebounce } from '@/hooks/useDebounce'
import { FormErrors } from '@/hooks/useForm'
import { cleanUrl } from '@/utils/cleanUrl'
import { LinkIcon, PlusIcon, ArrowIcon, BinIcon, TickIcon } from '@/icons'

import s from './CreateLinkInput.module.css'

type Props = {
  values: any
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  addDynamicField: any
  removeDynamicField: any
  onUrlChange: (e: ChangeEvent<HTMLInputElement>, index: number) => void
  errors: FormErrors
  slug?: string
  defaultValues?: { slug: string; destinations: string[] }
}

export const CreateLinkInput = ({
  values,
  onChange,
  onUrlChange,
  addDynamicField,
  removeDynamicField,
  errors,
  slug,
  defaultValues,
}: Props) => {
  const [value, setValue] = useDebounce('')
  const [url, setUrl] = useState(defaultValues?.destinations[0] ?? null)
  const destinationsFieldCount = values?.destinations?.length ?? 0

  useEffect(() => {
    setUrl(cleanUrl(value))
  }, [value])

  return (
    <Card className={s.container}>
      <div className={s.icon}>
        <IconContainer size="lg">
          {url && values.destinations?.length === 1 ? (
            <FaviconFetcher url={url} animate />
          ) : (
            LinkIcon
          )}
        </IconContainer>
      </div>

      <div className={s.fields}>
        <div className={s.topField}>
          {slug ? (
            <h2 className={s.slug}>g.to/{slug}</h2>
          ) : (
            <SlugInput
              name="slug"
              onChange={onChange}
              error={errors.slug}
              value={values.slug ?? defaultValues?.slug}
              autoFocus={!defaultValues?.slug}
            />
          )}
          {/\/\*/.test(values?.destinations[0]) && (
            <small className={s.tip}>{TickIcon} Variable Link</small>
          )}
        </div>
        {errors.slug && errors.slug !== '-' && <ErrorMsg text={errors.slug} />}
        <div>
          {values.destinations?.map((value: string, index: number) => {
            const error = errors[`destinations.${index}`]
            return (
              <div key={index} className={value ? '' : s.expand}>
                <div className={s.url}>
                  <span className={s.arrow}>{ArrowIcon}</span>
                  <Input
                    name="destinations"
                    type="url"
                    value={value}
                    onChange={(e) => {
                      onUrlChange(e, index)
                      setValue(e.target.value)
                    }}
                    error={error}
                    autoFocus={!!slug || !!defaultValues?.slug}
                    placeholder="Destination URL"
                    fullWidth
                  />
                  {index === destinationsFieldCount - 1 && (
                    <button
                      className={s.addBtn}
                      type="button"
                      disabled={destinationsFieldCount === 10}
                      onClick={() => addDynamicField('destinations')}
                    >
                      {PlusIcon}
                    </button>
                  )}
                  {destinationsFieldCount > 1 && (
                    <button
                      type="button"
                      className={s.removeBtn}
                      onClick={() => removeDynamicField('destinations', index)}
                    >
                      {BinIcon}
                    </button>
                  )}
                </div>
                {error && error !== '-' && (
                  <div className={s.urlError}>
                    <ErrorMsg text={error} matter={false} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </Card>
  )
}
